import { useState } from "react";

export interface AdminSongMeta {
  id: string;
  title: string;
  artist: string;
  releaseYear: number;
  artistType: string | null;
  genre: string | null;
  country: string | null;
  language: string | null;
}

interface Props {
  song: AdminSongMeta;
  onSaved: (song: AdminSongMeta) => void;
}

export function AdminSongMetaEditor({ song, onSaved }: Props) {
  const [artistType, setArtistType] = useState(song.artistType ?? "");
  const [genre, setGenre] = useState(song.genre ?? "");
  const [country, setCountry] = useState(song.country ?? "");
  const [language, setLanguage] = useState(song.language ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/admin/songs/${song.id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          artistType: artistType || null,
          genre: genre.trim() || null,
          country: country.trim() || null,
          language: language.trim() || null,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error ?? "Enregistrement impossible");
      }
      const updated = await res.json();
      onSaved(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 bg-violet-950/40 border-t border-violet-500/20 space-y-3">
      <p className="text-sm font-semibold text-violet-200">Métadonnées Bingo — {song.title}</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <label className="block text-sm">
          <span className="text-violet-300/70">Type d&apos;artiste</span>
          <select className="cw-input mt-1" value={artistType} onChange={(e) => setArtistType(e.target.value)}>
            <option value="">— non renseigné</option>
            <option value="solo">Solo</option>
            <option value="group">Groupe</option>
          </select>
        </label>
        <label className="block text-sm">
          <span className="text-violet-300/70">Genre</span>
          <input className="cw-input mt-1" value={genre} onChange={(e) => setGenre(e.target.value)} placeholder="optionnel" />
        </label>
        <label className="block text-sm">
          <span className="text-violet-300/70">Pays</span>
          <input className="cw-input mt-1" value={country} onChange={(e) => setCountry(e.target.value)} placeholder="optionnel" />
        </label>
        <label className="block text-sm">
          <span className="text-violet-300/70">Langue</span>
          <input className="cw-input mt-1" value={language} onChange={(e) => setLanguage(e.target.value)} placeholder="optionnel" />
        </label>
      </div>
      {error && <p className="text-sm text-red-400">{error}</p>}
      <button type="button" className="cw-btn cw-btn-primary" disabled={saving} onClick={() => void save()}>
        {saving ? "Enregistrement…" : "Enregistrer"}
      </button>
    </div>
  );
}

export function bingoCompatibilityBadges(song: AdminSongMeta) {
  const badges: Array<{ label: string; ok: boolean }> = [
    { label: "Frise", ok: true },
    { label: "Bingo année", ok: Number.isInteger(song.releaseYear) },
    { label: "Bingo artiste", ok: Boolean(song.artist?.trim()) },
    { label: "Type artiste", ok: song.artistType === "solo" || song.artistType === "group" },
    { label: "Genre", ok: Boolean(song.genre?.trim()) },
  ];
  return badges;
}
